import { createContext, useContext } from "react";

import type { fetchAuthSession } from "aws-amplify/auth";

export type CognitoUserSession = Awaited<ReturnType<typeof fetchAuthSession>>;

export enum AuthActionEnum {
  SignIn = "sign-in",
  EnterCode = "enter-code",
  SignOut = "sign-out",
  ResetPassword = "reset-password",
  ForgotPassword = "forgot-password",
}

export function isAuthAction(value?: string | null): value is AuthActionEnum {
  const actions: Array<string> = Object.values(AuthActionEnum);
  return value ? actions.includes(value) : false;
}

export type RemoteAuthSession = {
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
};

export type AuthorizationSession = {
  authorization: string;
  profile?: string;
};

// What a webapp may read of its session. Establishing one (password, 2FA,
// passkey, Google, PAT) belongs to the auth app and is not part of this
// contract.
export type AuthContextType = {
  session: CognitoUserSession | AuthorizationSession | RemoteAuthSession | null;
  jwt: string | null;
  pat: string | null;
  bearer: string | null;
  isLoggedIn: boolean;
  signOut(_?: { redirect?: string }): Promise<void>;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
